import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, X, Clock, CheckCircle, Users, Heart, RotateCcw, Loader2, Crown } from 'lucide-react';
import AnswerCard from './components/AnswerCard';
import FontSizeSelector from './components/FontSizeSelector';
import { usePaperFontSize, paperFontClass } from '../../utils/paperFontSize';
import QuestionRenderer from './components/QuestionRenderer';
import type { Question, Paper } from './types';
import type { QuestionCorrectRateInfo } from '../../utils/questionStats';
import { typeNames, subTypeNames } from '../../constants/questionTypes';
import { categoryStore } from '../../store/categoryStore';
import QuizQuestionTitle from '../../components/admin/QuizQuestionTitle';
import ReportErrorModal from '../../components/ReportErrorModal';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { loadKnowledgeTagData, type TagDef } from '../../utils/questionTag';
import { splitQuizPaperTitle, formatPaperTitle } from '../../utils/paperTitle';
import { BlueSuffixTitle } from '../../components/PaperTitleDisplay';

interface QuizReviewModeProps {
  paper: Paper;
  examQuestions: Question[];
  answers: Record<string, string>;
  score: number;
  timeLeft: number;
  objectiveScore?: number | null;
  objectiveTotalScore?: number | null;
  programmingScore?: number | null;
  questionFeedbacks?: Record<string, { score?: number; feedback?: string }> | null;
  currentUser?: any;
  paperId?: string;
  studentName?: string;
  hideScoreAndTime?: boolean;
  isWrongQuestionMode?: boolean;
  showAnswerModeButton?: boolean;
  onEnterAnswerMode?: () => void;
  onExitAnswerMode?: () => void;
  onCheckPermission?: () => void;
  checkingPermission?: boolean;
  isInAnswerMode?: boolean;
  source?: 'practice' | 'class' | 'exam'; // 新增：来源字段
  correctRateMap?: Record<string, QuestionCorrectRateInfo>; // 题目正确率数据（含答题人数）
  knowledgeTags?: TagDef[]; // 知识点标签（答案解析区只读展示；不传则组件按 paperId 自行加载）
  tagMarks?: Record<string, string[]>; // contentKey → 已标注 tagId 列表
  onBack: () => void;
}

const EXAM_DURATION = 60 * 60;

export default function QuizReviewMode({
  paper,
  examQuestions: initialQuestions,
  answers: initialAnswers,
  score,
  timeLeft,
  objectiveScore,
  objectiveTotalScore,
  programmingScore,
  questionFeedbacks,
  currentUser,
  paperId,
  studentName,
  hideScoreAndTime,
  isWrongQuestionMode,
  showAnswerModeButton,
  onEnterAnswerMode,
  onExitAnswerMode,
  onCheckPermission,
  checkingPermission,
  isInAnswerMode = false,
  source,
  correctRateMap,
  knowledgeTags,
  tagMarks,
  onBack
}: QuizReviewModeProps) {
  const navigate = useNavigate();
  const [examQuestions] = useState<Question[]>(initialQuestions);
  const [answers] = useState<Record<string, string>>(initialAnswers);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [fontSize, setFontSize] = usePaperFontSize();
  const [markedQuestions] = useState<Set<number>>(new Set());
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [showReportModal, setShowReportModal] = useState(false);
  // 知识点标签：仅真实试卷有标注数据；上层未显式传入时按 paperId 自行加载（错题回顾页会显式传入聚合数据）
  const [selfTags, setSelfTags] = useState<TagDef[]>([]);
  const [selfMarks, setSelfMarks] = useState<Record<string, string[]>>({});

  useEffect(() => {
    // 上层已显式传入（即使是空数组）则不再自行加载
    if (knowledgeTags && tagMarks) return;
    let cancelled = false;
    loadKnowledgeTagData(paperId || paper?.id || '', { categoryType: paper?.category_type, title: paper?.title }).then(data => {
      if (cancelled) return;
      setSelfTags(data.tags);
      setSelfMarks(data.marks);
    });
    return () => { cancelled = true; };
  }, [paperId, paper?.id, paper?.category_type, paper?.title, knowledgeTags, tagMarks]);

  const effectiveTags = knowledgeTags ?? selfTags;
  const effectiveMarks = tagMarks ?? selfMarks;

  // 标记错题已掌握
  const resolveWrongQuestion = async (questionId: string) => {
    if (!currentUser?.id) {
      navigate('/login');
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'resolve_wrong_question', question_id: questionId })
      });

      const result = await response.json();
      if (result.success) {
        setResolvedIds(prev => new Set([...prev, questionId]));
      } else {
        alert(result.message || '标记失败');
      }
    } catch {
      alert('操作失败，请稍后重试');
    }
  };

  // 初始化收藏数据
  useEffect(() => {
    const initFavorites = async () => {
      if (currentUser?.id) {
        const { data } = await supabase.from('user_favorites').select('question_id').eq('user_id', currentUser.id);
        if (data) {
          setFavoriteIds(new Set(data.map((f: any) => String(f.question_id))));
        }
      }
    };
    initFavorites();
  }, [currentUser?.id]);

  const currentQuestion = examQuestions[currentIndex];
  const isSubmitted = true;

  const programmingTypes = ['scratch_coding', 'python_coding', 'cpp_coding'];
  const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
  const hasProgrammingQuestions = examQuestions.some(q => programmingTypes.includes(q.type));
  const hasObjectiveQuestions = examQuestions.some(q => objectiveTypes.includes(q.type));

  // 判断编程题是否已批阅
  const programmingGraded = programmingScore !== undefined && programmingScore !== null;

  // 新增：判断是否应该显示"待批阅"
  const isAssignmentSource = source === 'class' || source === 'exam'; // 来自作业
  const shouldShowPending = isAssignmentSource && !programmingGraded; // 只有作业且未批阅才显示"待批阅"

  // 「待阅卷」编程题集合（用于答题卡题号底色）：作业来源 + 有编程题 + 整卷未批阅，
  // 且该题没有单题批阅得分（questionFeedbacks[q.id].score 为空）。
  // 这些题号在答题卡上不带红/绿底色（无底色 = 未阅卷，与答对/答错/未答视觉区分）。
  const pendingGrading = isAssignmentSource && hasProgrammingQuestions && !programmingGraded;
  const ungradedQuestionIds = new Set<string>(
    pendingGrading
      ? examQuestions
          .filter(q => programmingTypes.includes(q.type) && !(questionFeedbacks && questionFeedbacks[q.id] && questionFeedbacks[q.id].score != null))
          .map(q => q.id)
      : []
  );
  console.log('[复盘-答题卡] 作业来源:', isAssignmentSource, ', 有编程题:', hasProgrammingQuestions, ', 整卷已批阅:', programmingGraded, ', 未阅卷题号数:', ungradedQuestionIds.size);

  // 已批阅编程题的单题得分映射（questionId → 阅卷得分）：答题卡据此把得满分/及格的编程题题号显示为绿色，
  // 而不是因编程作答不写 answers 而误落入红色「未答」分支
  const gradedScores: Record<string, number> = {};
  if (questionFeedbacks) {
    for (const q of examQuestions) {
      const fb = (questionFeedbacks as Record<string, any>)[q.id];
      if (fb && typeof fb.score === 'number') gradedScores[q.id] = fb.score;
    }
  }
  console.log('[复盘-答题卡] 已批阅单题得分条数:', Object.keys(gradedScores).length, gradedScores);

  // 客观题分数：始终按（已用最新题目数据覆盖过的）examQuestions 重算
  // 这样后台修改正确答案后，即使 exam_records.objective_score 尚未同步，复盘页也显示最新判分
  const recalculateObjectiveScore = () => {
    let calculated = 0;
    for (const q of examQuestions) {
      if (!objectiveTypes.includes(q.type)) continue;
      const userAnswer = answers[q.id];
      if (!userAnswer) continue;
      if (q.type === 'multi_choice') {
        const selectedIds = userAnswer.split(',');
        const correctIds = q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id) || [];
        if (selectedIds.length === correctIds.length && selectedIds.every((id: string) => correctIds.includes(id))) {
          calculated += q.score || 0;
        }
      } else {
        const selectedOption = q.options?.find((o: any) => o.id === userAnswer);
        if (selectedOption?.isCorrect) calculated += q.score || 0;
      }
    }
    // 数据库保存的分数非空且与前端重算一致时采用数据库值；不一致时优先显示按最新答案的重算值
    if (objectiveScore != null && objectiveScore === calculated) return objectiveScore;
    return calculated;
  };
  const displayObjectiveScore = recalculateObjectiveScore();
  const displayProgrammingScore: number | string = programmingGraded
    ? (programmingScore as number)
    : (shouldShowPending ? '待批阅' : '-');
  // 总分 = 客观题得分 + 编程题得分
  const displayTotalScore: number | string = (hasProgrammingQuestions && !programmingGraded)
    ? '-'
    : (displayObjectiveScore + (programmingGraded ? (programmingScore as number) : 0));

  const getQuestionTypeName = (type: string, subType?: string) => {
    if (type === 'scratch_coding') return categoryStore.getCategoryNameByCode('scratch') || '图形化编程';
    if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
    return typeNames[type] || '单选题';
  };

  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const handleAnswerChange = () => {
    // 作答详情模式下不允许修改答案
  };

  const handleTextAnswerChange = () => {
    // 作答详情模式下不允许修改答案
  };

  const toggleFavorite = async (questionId: string) => {
    if (!currentUser?.id) {
      navigate('/login');
      return;
    }
    const isFav = favoriteIds.has(questionId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        navigate('/login');
        return;
      }

      if (isFav) {
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ action: 'remove_favorite', question_id: questionId })
        });
        const result = await response.json();
        if (result.success) {
          setFavoriteIds(prev => { const next = new Set(prev); next.delete(questionId); return next; });
        }
      } else {
        // paperId prop 可能为 undefined（如从收藏页面进入时），用 paper.id 作为 fallback
        const effectivePaperId = paperId || paper?.id || '';
        if (!effectivePaperId) {
          alert('无法收藏：缺少试卷信息');
          return;
        }
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ action: 'add_favorite', question_id: questionId, paper_id: effectivePaperId })
        });
        const result = await response.json();
        if (result.success) {
          setFavoriteIds(prev => new Set([...prev, questionId]));
        } else {
          alert(result.message || '收藏失败');
        }
      }
    } catch {
      alert('操作失败，请稍后重试');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs lg:text-sm">返回</span>
            </button>
          </div>
          <div className="flex items-center min-w-0 px-2 gap-[1cm]">
            {/* 页面类型标签 - 作答详情 */}
            {paper?.title && (
              <span className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                paper.title.includes('错题') || isWrongQuestionMode
                  ? 'bg-rose-50 text-rose-700 border border-rose-200 shadow-md shadow-rose-500/10 hover:shadow-rose-500/20'
                  : 'bg-blue-50 text-blue-700 border border-blue-200 shadow-md shadow-blue-500/10 hover:shadow-blue-500/20'
              }`}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  {paper.title.includes('错题') || isWrongQuestionMode ? (
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  ) : (
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                  )}
                </svg>
                {paper.title.includes('错题') || isWrongQuestionMode ? '我的错题' : Object.keys(answers).length > 0 ? '作答详情' : '我的收藏'}
              </span>
            )}
            {(() => {
              const cleanTitle = paper?.title?.split(' - 收藏题目')[0] || paper?.title?.split(' - 错题回顾')[0] || paper?.title?.split('图形化')[0] || '答题回顾';
              const { line1, line2 } = splitQuizPaperTitle(formatPaperTitle(cleanTitle, (paper as any)?.subtitle), paper as any);
              return (
                <>
                  <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
                    {line1 ? <BlueSuffixTitle text={line1} /> : '答题回顾'}
                  </h1>
                  {line2 && (
                    <span className="text-[11px] sm:text-xs lg:text-sm text-gray-500 truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
                      {line2}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {/* 答题模式按钮 - 收藏回顾页和错题回顾页显示 */}
            {showAnswerModeButton && (onEnterAnswerMode || onCheckPermission || onExitAnswerMode) && (
              <button
                onClick={isInAnswerMode ? onExitAnswerMode : (onCheckPermission || onEnterAnswerMode)}
                disabled={checkingPermission}
                className={`px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded font-medium transition-colors flex items-center gap-2 flex-shrink-0 shadow-sm ${
                  checkingPermission
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:from-amber-600 hover:to-orange-600'
                }`}
              >
                {checkingPermission ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isInAnswerMode ? (
                  <RotateCcw className="w-4 h-4" />
                ) : (
                  <Crown className="w-4 h-4" />
                )}
                {checkingPermission ? '检查中...' : (isInAnswerMode ? '回到复习模式' : '开启答题模式')}
              </button>
            )}
            {/* 教师查看标识 */}
            {studentName && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg border bg-blue-50 border-blue-200 mb-1">
                <Users className="w-3 h-3 text-blue-600" />
                <span className="text-blue-700 text-xs">
                  正在查看 <span className="font-semibold">{studentName}</span> 学员的答题记录
                </span>
              </div>
            )}
            {!hideScoreAndTime && (
              <>
                {/* 分项得分 - 横向排列 */}
                <div className="flex items-center gap-2">
                  {hasObjectiveQuestions && (
                  <div className="flex items-center gap-1 px-2 lg:px-3 py-1 rounded-lg border bg-blue-50 border-blue-200">
                    <span className="text-blue-700 text-xs lg:text-sm">客观题：</span>
                    <span className="text-blue-700 font-bold text-sm lg:text-base">{displayObjectiveScore}分</span>
                  </div>
                  )}
                  {hasProgrammingQuestions && (
                    <div className={`flex items-center gap-1 px-2 lg:px-3 py-1 rounded-lg border ${
                      displayProgrammingScore === '待批阅'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-purple-50 border-purple-200'
                    }`}>
                      <span className={`text-xs lg:text-sm ${
                        displayProgrammingScore === '待批阅'
                          ? 'text-amber-700'
                          : 'text-purple-700'
                      }`}>编程题：</span>
                      <span className={`font-bold text-sm lg:text-base ${
                        displayProgrammingScore === '待批阅'
                          ? 'text-amber-600'
                          : 'text-purple-700'
                      }`}>
                        {displayProgrammingScore === '待批阅' ? '待批阅' : displayProgrammingScore === '-' ? '-' : `${displayProgrammingScore}分`}
                      </span>
                    </div>
                  )}
                  <div className={`flex items-center gap-1 px-2 lg:px-3 py-1 rounded-lg border ${
                    displayTotalScore === '-' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'
                  }`}>
                    <span className={`text-xs lg:text-sm ${
                      displayTotalScore === '-' ? 'text-gray-500' : 'text-red-700'
                    }`}>总分：</span>
                    <span className={`font-bold text-sm lg:text-base ${
                      displayTotalScore === '-' ? 'text-gray-400' : 'text-red-600'
                    }`}>
                      {displayTotalScore === '-' ? '-' : `${displayTotalScore}分`}
                    </span>
                  </div>
                </div>
                {/* 用时 - 小字显示在下方 */}
                <div className="flex items-center gap-1 text-gray-500 text-xs">
                  <Clock className="w-3 h-3" />
                  <span className="font-mono">
                    {(() => {
                      const usedTime = EXAM_DURATION - timeLeft;
                      const h = Math.floor(usedTime / 3600);
                      const m = Math.floor((usedTime % 3600) / 60);
                      const s = usedTime % 60;
                      if (h > 0) return `用时：${h}小时${m}分${s}秒`;
                      return `用时：${m}分${s}秒`;
                    })()}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        {/* Mobile Answer Card Button */}
        <button onClick={() => setShowSidebar(true)} className="lg:hidden fixed bottom-4 right-4 z-40 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Sidebar - Answer Card */}
          <div className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
            <AnswerCard
              examQuestions={examQuestions}
              currentIndex={currentIndex}
              answers={answers}
              markedQuestions={markedQuestions}
              isSubmitted={isSubmitted}
              paper={paper}
              onQuestionSelect={setCurrentIndex}
              ungradedQuestionIds={ungradedQuestionIds}
              gradedScores={gradedScores}
            />
            <div className="mt-3">
              <FontSizeSelector value={fontSize} onChange={setFontSize} />
            </div>
          </div>

          {/* Question Area */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4">
            {/* 字号缩放只作用于题目内容（题干+选项），外层框架 UI 保持固定字号 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Question Header */}
              <div className="sticky lg:top-24 z-30 bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 border-b border-blue-200">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
                      {getQuestionTypeName(currentQuestion?.type, currentQuestion?.subType)}
                    </span>
                    {/* 上一题/下一题按钮 */}
                    <div className="hidden sm:flex items-center gap-2">
                      <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                        <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                      </button>
                      <button onClick={() => setCurrentIndex(Math.min(examQuestions.length - 1, currentIndex + 1))} disabled={currentIndex === examQuestions.length - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                        下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                  {/* 收藏按钮 */}
                  {currentUser?.id && currentQuestion?.id && (
                    <button
                      onClick={() => toggleFavorite(currentQuestion.id)}
                      className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg border text-xs lg:text-sm font-medium transition-all flex items-center gap-1.5 ${
                        favoriteIds.has(currentQuestion.id)
                          ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-red-300 hover:text-red-500'
                      }`}
                    >
                      <Heart className={`w-3 h-3 lg:w-4 lg:h-4 ${favoriteIds.has(currentQuestion.id) ? 'fill-current' : ''}`} />
                      {favoriteIds.has(currentQuestion.id) ? '已收藏' : '收藏'}
                    </button>
                  )}
                  {/* 错题模式：标记已掌握按钮 */}
                  {isWrongQuestionMode && currentUser?.id && currentQuestion?.id && (
                    <button
                      onClick={() => resolveWrongQuestion(currentQuestion.id)}
                      disabled={resolvedIds.has(currentQuestion.id)}
                      className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg border text-xs lg:text-sm font-medium transition-all flex items-center gap-1.5 ${
                        resolvedIds.has(currentQuestion.id)
                          ? 'bg-green-50 border-green-200 text-green-600 cursor-default'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-green-300 hover:text-green-500'
                      }`}
                    >
                      <CheckCircle className={`w-3 h-3 lg:w-4 lg:h-4 ${resolvedIds.has(currentQuestion.id) ? 'fill-current' : ''}`} />
                      {resolvedIds.has(currentQuestion.id) ? '已掌握' : '从错题中移除'}
                    </button>
                  )}
                </div>
              </div>

              {/* Question Content */}
              <div className={`p-3 sm:p-4 lg:p-6 border-b border-gray-100 ${paperFontClass(fontSize)}`}>
                <QuizQuestionTitle
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  examQuestions={examQuestions}
                  isPreview={false}
                  showCodingTitle={true}
                />
              </div>

              {/* Answer Area - Review Mode */}
              <div className={`p-3 sm:p-4 lg:p-6 ${paperFontClass(fontSize)}`}>
                <QuestionRenderer
                  key={currentQuestion?.id}
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  answers={answers}
                  isSubmitted={true}
                  isPreview={false}
                  paperId={paperId}
                  paper={paper}
                  currentUser={currentUser}
                  examQuestions={examQuestions}
                  isAssignmentMode={false}
                  questionFeedbacks={questionFeedbacks}
                  correctRateMap={correctRateMap}
                  knowledgeTags={effectiveTags}
                  tagMarks={effectiveMarks}
                  onAnswerChange={handleAnswerChange}
                  onTextAnswerChange={handleTextAnswerChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.3 }} className="fixed inset-y-0 right-0 w-[85vw] sm:w-80 bg-white shadow-xl z-50 lg:hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">答题卡</h2>
                <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-600" /></button>
              </div>
              <div className="p-4 overflow-y-auto">
                <AnswerCard
                  examQuestions={examQuestions}
                  currentIndex={currentIndex}
                  answers={answers}
                  markedQuestions={markedQuestions}
                  isSubmitted={isSubmitted}
                  paper={paper}
                  onQuestionSelect={setCurrentIndex}
                  onCloseSidebar={() => setShowSidebar(false)}
                  ungradedQuestionIds={ungradedQuestionIds}
                  gradedScores={gradedScores}
                />
                <div className="mt-3">
                  <FontSizeSelector value={fontSize} onChange={setFontSize} />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 纠错弹窗 */}
      <ReportErrorModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        questionId={examQuestions[currentIndex]?.id || ''}
        questionTitle={formatPaperTitle(paper?.title, (paper as any)?.subtitle) || '未知试卷'}
        paperId={paperId}
        currentUser={currentUser}
        question={examQuestions[currentIndex] ? {
          id: examQuestions[currentIndex].id,
          title: examQuestions[currentIndex].title,
          order_index: currentIndex + 1,
          question_type: examQuestions[currentIndex].type,
          options: examQuestions[currentIndex].options,
          reference_answer: (examQuestions[currentIndex] as any).referenceAnswer,
          explanation: (examQuestions[currentIndex] as any).explanation,
          scoring_points: (examQuestions[currentIndex] as any).scoringPoints,
          title_images: (examQuestions[currentIndex] as any).titleImages,
          title_image_widths: (examQuestions[currentIndex] as any).titleImageWidths,
          title_image_layout: (examQuestions[currentIndex] as any).titleImageLayout,
          image_position: (examQuestions[currentIndex] as any).imagePosition,
          option_layout: (examQuestions[currentIndex] as any).optionLayout,
        } : null}
      />
    </div>
  );
}
